import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { RawEditAnalyticsEvent } from '@gitbook/app-analytics/client';
import { isImageFile, type NativeFile } from '@gitbook/app-database';
import {
    BLOCK_DRAWING,
    BLOCK_FILE,
    BLOCK_IMAGES,
    type BlockDrawingData,
    canInsertBlockAt,
    canInsertInlineAt,
    files,
    INLINE_IMAGE,
    images,
    isPureText,
} from '@gitbook/doc-core';
import {
    defaultParsingOptions,
    looksLikeMarkdown,
    type ParsingFileOptions,
    type ParsingOptions,
    parseHTML,
    parseMarkdown,
} from '@gitbook/doc-format';
import { useHiveBranch } from '@gitbook/hive-react';
import type { Change, Document, Fragment } from '@gitbook/slate';
import { type EditorContainer, getEventTransfer, type Plugin } from '@gitbook/slate-react';
import { toast } from '@gitbook/spine-react';
import { Logger, throwIfNotExposable } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';
import { isDataUrl, isUrl, parseDataUrl } from '@gitbook/util-url';

import { useUploadFiles } from '../../Files';
import type { ContentEditorContextValue } from '../contexts';
import {
    GITBOOK_FILES_CONTENT_TYPE,
    type GitBookFilesTransferData,
    GitBookFilesTransferDataSchema,
    parseTransferData,
} from '../types';

const TOAST_ID = 'upload-files';

function notifyUploadError(error: Error) {
    toast.error('Error while pasting content', error, {
        id: TOAST_ID,
    });
    throwIfNotExposable(error);
}

function notifyUploadProgress(count: number) {
    toast.loading(`Uploading ${count} file${count !== 1 ? 's' : ''}…`, {
        id: TOAST_ID,
        description: 'This might take a moment.',
    });
}

function dismissNotifyUpload() {
    toast.dismiss(TOAST_ID);
}

const logger = Logger('app:usePastePlugin');

/**
 * Create slate plugin to handle paste from the user.
 */
export function usePastePlugin(editorContext: ContentEditorContextValue): Plugin {
    const {
        logAnalyticsEvent,
        blocks: { withImages, withFiles },
    } = editorContext;

    const hiveBranch = useHiveBranch();
    const { insertFiles, uploadFiles, uploadAndInsertFiles } = useUploadFiles();

    const duplicateFile = async (fileMetadata: {
        downloadURL: string;
        name: string;
        contentType: string;
    }) => {
        const response = await fetch(fileMetadata.downloadURL);
        const blob = await response.blob();
        const file = new File([blob], fileMetadata.name, {
            type: fileMetadata.contentType,
        });
        const [clonedMetadata] = await uploadFiles([file]);
        return clonedMetadata;
    };

    /**
     * Transform the fragment before inserting it into the editor.
     * - Duplicate drawing files
     */
    const transformFragmentBeforeInsert = async (fragment: Fragment): Promise<Fragment> => {
        if (!hiveBranch) {
            return fragment;
        }

        const fileIds = Array.from(
            new Set(
                fragment.nodes
                    .map((node) => {
                        if (node.type === BLOCK_DRAWING) {
                            const blockData = node.data.toFrozenObject() as BlockDrawingData;
                            return blockData.ref?.file ?? null;
                        }
                        return null;
                    })
                    .toArray()
                    .filter((fileId) => fileId !== null)
            )
        );

        if (fileIds.length === 0) {
            return fragment;
        }

        notifyUploadProgress(fileIds.length);

        const filesMetadata = await Promise.all(
            fileIds.map(async (fileId) => {
                const file = hiveBranch.revision.files[fileId];
                assertIsDefined(file, `File ${fileId} not found in current revision`);
                const metadata = await duplicateFile(file);
                return metadata;
            })
        );

        const fileRefs = await insertFiles(filesMetadata);
        const fileRefMap = new Map(
            fileIds.map((fileId, index) => {
                const fileRef = fileRefs[index];
                assertIsDefined(fileRef, `File ${fileId} not found in uploaded files`);
                return [fileId, fileRef];
            })
        );

        return fragment.set(
            'nodes',
            fragment.nodes.map((node) => {
                if (node.type === BLOCK_DRAWING) {
                    const blockData = node.data.toFrozenObject() as BlockDrawingData;
                    if (blockData.ref) {
                        const fileRef = fileRefMap.get(blockData.ref.file);
                        assertIsDefined(
                            fileRef,
                            `File ref ${blockData.ref.file} not found in uploaded files`
                        );
                        return node.setData(['ref'], fileRef);
                    }
                }
                return node;
            })
        );
    };

    /**
     * Return any files referenced by the given node.
     */
    const handleFileTransferData = async (files: GitBookFilesTransferData) => {
        const toUploadFiles = Object.keys(files).filter((fileId) => {
            // If this fileId is already in current revision, skip it.
            return !hiveBranch?.revision.files[fileId];
        });

        if (!toUploadFiles.length) {
            return;
        }

        notifyUploadProgress(toUploadFiles.length);

        // Download every file and re-upload it in the new location.
        const metadatas = await Promise.all(
            toUploadFiles.map(async (fileId) => {
                const fileMetadata = files[fileId];
                const metadata = await duplicateFile(fileMetadata);
                return { id: fileId, ...metadata };
            })
        );

        await insertFiles(metadatas);
    };

    /*
     * Handle paste or drop of files.
     */
    const onInsert = useEventCallback(
        (
            event: ClipboardEvent,
            _change: Change,
            editor: EditorContainer
        ): Change | boolean | undefined => {
            const transfer = getEventTransfer(event);
            let uploadingFiles = 0;

            const resolveFile = async (
                src: string,
                options?: ParsingFileOptions
            ): Promise<api.ContentRefFile | api.ContentRefURL | null> => {
                if (isUrl(src)) {
                    return { kind: 'url', url: src };
                }

                if (isDataUrl(src)) {
                    const decodedDataURL = parseDataUrl(src);
                    if (!decodedDataURL) {
                        return null;
                    }

                    if (!decodedDataURL.mimeType) {
                        logger.error('Could not detect mime type from data URL');
                        return null;
                    }

                    const extension = decodedDataURL.mimeType.split('/').at(1);
                    if (!options?.fileName && !extension) {
                        logger.error('Could not detect filename when resolving file');
                        return null;
                    }
                    const name = options?.fileName || `unknown.${extension}`;
                    const file = new File([decodedDataURL.data], name, {
                        type: decodedDataURL.mimeType,
                    });

                    uploadingFiles += 1;
                    notifyUploadProgress(uploadingFiles);
                    const uploaded = await uploadAndInsertFiles([file]);

                    return uploaded[0];
                }

                return null;
            };

            const parsingOptions: ParsingOptions = {
                ...defaultParsingOptions,
                resolveFile,
            };

            // HTML
            if (transfer.type === 'html' && transfer.html !== null) {
                parseHTML(transfer.html, parsingOptions)
                    .then(({ document }) => {
                        // Sometimes, Markdown is copied from a website or code editor
                        // which will copy it as HTML (to include syntax highlighting).
                        // If the HTML does not yield more than simple text, it means it
                        // could be Markdown
                        if (isPureText(document) && transfer.text) {
                            // Parse it as Markdown instead
                            return parseTextOrMarkdown(transfer.text, parsingOptions).then(
                                ({ document }) => {
                                    return {
                                        document,
                                        type: 'text',
                                    };
                                }
                            );
                        }
                        return { document, type: 'html' };
                    })
                    .then(({ document, type }: { document: Document; type: 'html' | 'text' }) => {
                        editor.change((change) => {
                            change.insertDocument(document);
                        });
                        dismissNotifyUpload();

                        const sourceIncludes = getPasteIncludesAnalyticsProps(
                            type === 'html' ? transfer.html! : transfer.text!,
                            type
                        );

                        logAnalyticsEvent(
                            getAnalyticsPasteEventProps(event, type, { document, sourceIncludes })
                        );
                    })
                    .catch(notifyUploadError);

                return true;
            }

            // Markdown
            if (transfer.type === 'text' && transfer.text !== null) {
                parseTextOrMarkdown(transfer.text, parsingOptions)
                    .then(({ document }) => {
                        editor.change((change) => {
                            change.insertDocument(document);
                        });
                        dismissNotifyUpload();

                        const sourceIncludes = getPasteIncludesAnalyticsProps(
                            transfer.text!,
                            'text'
                        );

                        logAnalyticsEvent(
                            getAnalyticsPasteEventProps(event, 'text', {
                                document,
                                sourceIncludes,
                            })
                        );
                    })
                    .catch(notifyUploadError);

                return true;
            }

            // Files & images
            if (transfer.type === 'files' && transfer.files) {
                const [imageFiles, otherFiles] = transfer.files.reduce<
                    [NativeFile[], NativeFile[]]
                >(
                    ([images, files], file) => {
                        if (isImageFile(file)) {
                            if (withImages) {
                                return [images.concat(file), files];
                            }
                        } else {
                            if (withFiles) {
                                return [images, files.concat(file)];
                            }
                        }

                        return [images, files];
                    },
                    [[], []]
                );

                // Nothing to paste (or nothing supported by the editor)
                if (imageFiles.length === 0 && otherFiles.length === 0) {
                    return;
                }

                const areAllImages = imageFiles.length > 0 && otherFiles.length === 0;

                // Check insertion compatibility BEFORE uploading
                const { canInsertAsBlock, canInsertAsInline } = (() => {
                    if (areAllImages) {
                        // Check if we can insert the images as an `images` block
                        const canInsertAsBlock = canInsertBlockAt(editor.value, BLOCK_IMAGES, {
                            // We only insert as a block image if we can replace an entire empty block
                            // otherwise we insert as inline images
                            allowSplitBlock: false,
                        });

                        const canInsertAsInline = canInsertInlineAt(editor.value, INLINE_IMAGE);

                        return {
                            canInsertAsBlock,
                            canInsertAsInline,
                        };
                    } else if (otherFiles.length > 0) {
                        // Check if we can insert file blocks
                        return {
                            canInsertAsBlock: canInsertBlockAt(editor.value, BLOCK_FILE),
                            canInsertAsInline: false,
                        };
                    }

                    return {
                        canInsertAsBlock: false,
                        canInsertAsInline: false,
                    };
                })();

                if (!canInsertAsBlock && !canInsertAsInline) {
                    toast(`Cannot paste ${areAllImages ? 'images' : 'files'} here`);
                    return;
                }

                const promise = Promise.all([
                    uploadAndInsertFiles(imageFiles),
                    uploadAndInsertFiles(otherFiles),
                ]);
                promise
                    .then(([imageRefs, fileRefs]) => {
                        const totalRefsCount = imageRefs.length + fileRefs.length;

                        // Uploads may have failed / been ignored because of their size/file type. We should've already
                        // told the user.
                        if (totalRefsCount === 0) {
                            return;
                        }

                        const areAllImages = imageRefs.length && !fileRefs.length;

                        editor.change((change) => {
                            if (areAllImages) {
                                if (canInsertAsBlock) {
                                    change
                                        .insertOrReplaceBlock(images.createImagesBlock(imageRefs))
                                        .focus();
                                    return;
                                }

                                if (canInsertAsInline) {
                                    imageRefs.reduce(
                                        (acc, ref) =>
                                            acc.insertInline(images.createImageInline(ref)),
                                        change
                                    );
                                    return;
                                }
                            }

                            [...imageRefs, ...fileRefs].forEach((ref) => {
                                if (ref.kind !== 'file') {
                                    return;
                                }

                                if (canInsertAsBlock) {
                                    const fileBlock = files.createFileBlock(ref);
                                    change.insertOrReplaceBlock(fileBlock).focus();
                                    return;
                                }
                            });
                        });

                        logAnalyticsEvent(
                            getAnalyticsPasteEventProps(event, 'files', {
                                uploadedFiles: totalRefsCount,
                                sourceIncludes: ['file'],
                            })
                        );
                    })
                    .catch(notifyUploadError);
            }

            // Slate documents
            if (transfer.type === 'fragment' && typeof transfer.fragment !== 'undefined') {
                try {
                    // A Slate document may have been copied with references to files. Ensure
                    // the files are copied to the new content.
                    const fragment = transfer.fragment;
                    const files = parseTransferData(
                        transfer.others[GITBOOK_FILES_CONTENT_TYPE],
                        GitBookFilesTransferDataSchema
                    );

                    const fileCount = files ? Object.keys(files).length : 0;
                    if (fileCount > 0) {
                        assertIsDefined(files);
                        handleFileTransferData(files)
                            .then(() => transformFragmentBeforeInsert(fragment))
                            .then((transformedFragment) => {
                                editor.change((change) =>
                                    change.insertDocument(transformedFragment)
                                );
                                dismissNotifyUpload();
                            })
                            .catch(notifyUploadError);

                        // We've handled the copy ourselves, don't go further into the plugin stack.
                        return true;
                    }
                } catch (err) {
                    notifyUploadError(err);
                }
            }
        }
    );

    return React.useMemo(() => ({ onPaste: onInsert, onDrop: onInsert }), [onInsert]);
}

/**
 * Parse plain text or Markdown.
 */
async function parseTextOrMarkdown(text: string, options: ParsingOptions) {
    if (looksLikeMarkdown(text)) {
        return parseMarkdown(text, options);
    }

    // If the text looks like plain text, we parse it in a loose way
    // preserving the soft-breaks.
    return parseMarkdown(text, {
        ...options,
        preserveSoftBreaks: true,
    });
}

/**
 * Get a list of "interesting" elements included in the clipboard.
 * Cheaply scan for existing elements in source.
 */
function getPasteIncludesAnalyticsProps(source: string, type: 'text' | 'html') {
    const includes: string[] = [];

    switch (type) {
        case 'text': // parsed as markdown
            if (source.includes('![')) {
                includes.push('image');
            }

            // matches |---, | ---, | :---
            if (source.match(/\|\s*:?---/)) {
                includes.push('table');
            }

            break;

        case 'html':
            if (source.includes('<img')) {
                includes.push('image');
            }

            if (source.includes('<table')) {
                includes.push('table');
            }

            break;
    }

    return includes;
}

/**
 * Construct paste event analytics properties.
 */
export function getAnalyticsPasteEventProps(
    event: ClipboardEvent,
    transformType: string,
    {
        document = undefined,
        uploadedFiles = 0,
        sourceIncludes = [],
    }: {
        document?: Document;
        uploadedFiles?: number;
        sourceIncludes?: string[];
    } = {}
): RawEditAnalyticsEvent {
    const dataTransfer = event.clipboardData;

    const transformNodes = new Set<string>();
    let transformSize = 0;

    if (document) {
        document.forEachDescendant((node) => {
            transformSize += 1;
            if (node.type) {
                transformNodes.add(node.type);
            }
        });
    }

    return {
        type: 'edit_paste_content',
        paste: event.type as 'paste' | 'drop',
        sourceTypes: [...(dataTransfer?.types || [])],
        sourceIncludes: [...new Set(sourceIncludes)],
        transformType,
        transformSize,
        uploadedFiles,
        transformNodes: [...transformNodes],
    };
}
